import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, ArrowRight, BookOpen, Sparkles, CheckCircle2, ScrollText } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { DecorativeDivider, LotusDivider, OmMark, BilvaLeaf, SriYantra } from "@/components/Ornaments";

const BLOCK_ICONS = {
  introduction: ScrollText,
  significance: Sparkles,
  scriptural_importance: BookOpen,
  who_should_perform: CheckCircle2,
  procedure: Sparkles,
  required_preparation: CheckCircle2,
};

export default function PoojaDetail() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [pooja, setPooja] = useState(null);

  useEffect(() => {
    api.get(`/poojas/${slug}`).then(({ data }) => setPooja(data)).catch(() => setPooja(false));
  }, [slug]);

  if (pooja === null) return <div className="container-x section">Loading...</div>;
  if (pooja === false) return <div className="container-x section">Pooja not found.</div>;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (pooja.faqs || []).map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <div data-testid={`pooja-detail-${slug}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* HERO */}
      <section className="relative min-h-[68vh] flex items-end overflow-hidden">
        <img src={pooja.image_url} alt={pooja.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <SriYantra size={520} className="text-[#f3d7a8]" opacity={0.12} />
        </div>
        <div className="container-x relative z-10 py-16 md:py-20 text-[#faf8f5]">
          <Link to="/poojas" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] opacity-80 hover:opacity-100" data-testid="back-to-poojas">
            <span>←</span> All Poojas
          </Link>
          <div className="mt-3 flex items-center gap-3 text-[#f3d7a8]">
            <OmMark size={26} className="text-[#f3d7a8]" />
            <span className="italic-serif text-base">Sacred Vedic Ritual</span>
          </div>
          <h1 className="mt-3 font-heading text-4xl md:text-7xl tracking-display max-w-4xl leading-[1.02]">{pooja.name}</h1>
          <p className="mt-4 max-w-2xl opacity-90 text-[15px] md:text-base">{pooja.short_description}</p>
          <div className="mt-6 flex flex-wrap gap-3 items-center text-sm">
            <span className="inline-flex items-center gap-2 bg-[#faf8f5]/15 backdrop-blur px-4 py-2 rounded-full"><Clock size={14} />{pooja.duration}</span>
            <span className="inline-flex items-center gap-2 bg-[#faf8f5]/15 backdrop-blur px-4 py-2 rounded-full">
              {t("poojas.startingFrom")} {t("common.rupee")}{Number(pooja.price).toLocaleString("en-IN")}
            </span>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="section bg-manuscript relative overflow-hidden">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 space-y-14">
            {pooja.introduction && (
              <Block icon={BLOCK_ICONS.introduction} title={t("poojas.introduction")} text={pooja.introduction} dropCap />
            )}
            {pooja.significance && (
              <Block icon={BLOCK_ICONS.significance} title={t("poojas.significance")} text={pooja.significance} />
            )}
            {pooja.scriptural_importance && (
              <Block icon={BLOCK_ICONS.scriptural_importance} title={t("poojas.scripturalImportance")} text={pooja.scriptural_importance} />
            )}
            {pooja.who_should_perform && (
              <Block icon={BLOCK_ICONS.who_should_perform} title={t("poojas.whoShould")} text={pooja.who_should_perform} />
            )}

            {pooja.benefits?.length > 0 && (
              <div>
                <DecorativeDivider label="Spiritual Merit" className="mb-4 justify-start" />
                <h2 className="font-heading text-3xl md:text-5xl tracking-display">{t("poojas.benefits")}</h2>
                <div className="gilt-rule my-5 w-20" />
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {pooja.benefits.map((b, i) => (
                    <li key={i} className="flex gap-3 items-start">
                      <BilvaLeaf size={18} className="text-[color:var(--brand)] mt-0.5 flex-shrink-0" />
                      <span className="opacity-90">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pooja.procedure && <Block icon={BLOCK_ICONS.procedure} title={t("poojas.procedure")} text={pooja.procedure} />}
            {pooja.required_preparation && <Block icon={BLOCK_ICONS.required_preparation} title={t("poojas.preparation")} text={pooja.required_preparation} />}

            {pooja.faqs?.length > 0 && (
              <div>
                <DecorativeDivider label="Common Questions" className="mb-4 justify-start" />
                <h2 className="font-heading text-3xl md:text-5xl tracking-display">{t("poojas.faqs")}</h2>
                <div className="gilt-rule my-5 w-20" />
                <Accordion type="single" collapsible className="mt-5 space-y-3">
                  {pooja.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`f-${i}`} className="border border-[color:var(--border-strong)] rounded-md bg-white px-6 data-[state=open]:border-[color:var(--brand)]/50">
                      <AccordionTrigger className="text-left font-heading text-xl md:text-2xl tracking-display py-5 hover:no-underline">
                        <span className="flex items-baseline gap-3">
                          <span className="italic-serif text-[color:var(--brand)]/60 text-sm">{String(i + 1).padStart(2, "0")}</span>
                          {f.question}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="opacity-85 leading-[1.85] pb-5 pl-[1.8rem] text-[15px]">{f.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start space-y-5">
            <div className="frame-gilt">
              <div className="p-6">
                <DecorativeDivider label={t("poojas.pricing")} className="mb-3 justify-start" />
                <div className="flex items-baseline gap-2">
                  <span className="font-heading italic-serif text-5xl text-[color:var(--brand)]">{t("common.rupee")}{Number(pooja.price).toLocaleString("en-IN")}</span>
                </div>
                <div className="text-[11px] opacity-65 mt-2 leading-relaxed italic-serif">{t("poojas.depositInfo")}</div>
                <div className="divider-paper my-5" />
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="opacity-65">Duration</span><span className="font-medium">{pooja.duration}</span></div>
                </div>
                <Link
                  to={`/book?pooja=${pooja.slug}`}
                  data-testid={`book-${pooja.slug}-btn`}
                  className="mt-6 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full btn-brand text-sm font-medium"
                >
                  {t("poojas.bookCta")} <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            <div className="card-manuscript rounded-md p-6">
              <OmMark size={28} className="text-[color:var(--brand)] mb-3" />
              <div className="font-heading text-xl">Need Guidance?</div>
              <p className="mt-2 text-sm opacity-80 leading-relaxed">Our acharyas can advise on the right ritual, timing and required preparation.</p>
              <Link to="/contact" className="mt-4 inline-flex items-center gap-1 text-sm text-[color:var(--brand)] hover:underline">Contact Acharya Guruji <ArrowRight size={12} /></Link>
            </div>
          </aside>
        </div>
      </section>
      <LotusDivider className="py-10 bg-paper" />
    </div>
  );
}

function Block({ icon: Icon, title, text, dropCap }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3 text-[color:var(--brand)]">
        <Icon size={18} />
        <span className="text-[10px] uppercase tracking-[0.28em] opacity-80">Section</span>
      </div>
      <h2 className="font-heading text-3xl md:text-5xl tracking-display leading-[1.05]">{title}</h2>
      <div className="gilt-rule my-5 w-20" />
      <p className={`leading-[1.85] text-[color:var(--ink-2)] text-[16px] whitespace-pre-line ${dropCap ? "drop-cap" : ""}`}>{text}</p>
    </div>
  );
}
